import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { message, Modal, Alert } from "antd";
import { artefactFileBasename, artefactFileNameUiDisplay } from "../utils/artefactDisplayFileName";
import { apiConfig } from "../authConfig";
import { useApp } from "../context/AppContext";
import { ArtefactTemplatePreview, DOCX_PREVIEW_BODY_CLASS } from "./ArtefactTemplatePreview";
import { isLikelyDocxFile, isLikelyPdfFile } from "../utils/blobPreview";
import { isCompanyArtefactsReleasedToDelegates } from "../utils/organizationalSetupWizard";

/** Prevents a hung network or server from leaving the UI stuck indefinitely (no default fetch timeout). */
const DELEGATE_SUBMIT_FETCH_TIMEOUT_MS = 240000;

async function fetchPostSubmitDelegate(projectId, templateRowId, token, signal) {
  const res = await fetch(
    `${apiConfig.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/organizational-setup/company-artefact-delegate-submit`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ templateRowId: String(templateRowId) }),
      signal,
    },
  );
  const json = await res.json().catch(() => ({}));
  return { res, json };
}

/**
 * Task #211-EVIDENCE-BLOB-SAS-GAP — `organizationalSetupWizard.companyArtefactFilledBlobUrlByRowId`
 * moved from a bare durable-blob-URL string to `{ blobUrl, readUrl }` (readUrl already
 * signed server-side, tenant-scoped, short-lived -- see `signBlobUrlMapForResponse` in
 * policylens-backend/src/controllers/shared/routeHandlerContext.ts). This resolves EITHER
 * shape to an opened tab, so a Company Delegate re-opening their own already-submitted
 * artefact keeps working under both the new response and any stale-cached/legacy value:
 *
 *  - new shape with a signed `readUrl`: open it directly -- it is already a valid SAS URL,
 *    and routing it back through `openBlobUrl`'s `/api/v1/blob-sas?url=` lookup (which
 *    expects the RAW durable blob URL, not one that already carries a SAS query string)
 *    would be both redundant and liable to fail the lookup.
 *  - new shape with `readUrl: null` (per-row sign failure -- cross-tenant mismatch or
 *    credential error, fails closed server-side): fall back to the durable `blobUrl`
 *    through the normal `openBlobUrl` re-sign round trip, same as the legacy path below,
 *    rather than treating the whole button as dead.
 *  - legacy bare-string shape (stale cached profile / older code path): treat it as the
 *    durable `blobUrl` and let `openBlobUrl`'s existing re-sign round trip handle it,
 *    exactly as before this task.
 *
 * Never falls back to opening a raw/unsigned URL directly (Commandment III) -- `blobUrl`
 * only ever reaches the browser via `openBlobUrl`'s own signing call.
 */
async function openFilledArtefactUrl(raw, openBlobUrl) {
  if (!raw) return false;
  if (typeof raw === "string") {
    return openBlobUrl ? openBlobUrl(raw) : false;
  }
  if (typeof raw === "object") {
    if (raw.readUrl) {
      window.open(raw.readUrl, "_blank", "noopener");
      return true;
    }
    if (raw.blobUrl) {
      return openBlobUrl ? openBlobUrl(raw.blobUrl) : false;
    }
  }
  return false;
}

/**
 * Non-blocking transparency banner for a Delegate's submission (Task #60).
 * Surfaces `stamped.warnings` from the branding/stamping pipeline on the
 * per-document submit response -- e.g. partial AI scoring, a failed batch,
 * a low-confidence flag (see .claude/lessons/silent-zero-scoring-ai-fallback.md,
 * the sibling pattern this mirrors for the gap-analysis screen in
 * GapAnalysisAiFailureBanner.jsx).
 *
 * Persona rule (CLAUDE.md): a Delegate answers questions, uploads evidence,
 * and contextualises templates -- they never approve anything and cannot
 * themselves fix an AI-scoring issue. This banner is therefore pure
 * transparency, never a gate: it renders only AFTER the submit call has
 * already succeeded and the artefact is recorded. Contrast with Task #76's
 * LA/Advisor finalize guard, which blocks on warnings -- that is a
 * different persona with the privilege to act on them.
 *
 * BACKEND GAP (found 2026-09-08, out of scope for this change): the handler
 * for this action, `projects_id_organizational_setup_company_artefact_delegate_submitPost`
 * (policylens-backend/src/controllers/workbench.controller.ts, submit block
 * ~L4953-5109), computes `stamped.warnings` at L5066 but never includes it
 * in the `sendSuccess` body at L5099-5104 -- unlike the two sibling
 * `stampProjectBranding` call sites (internalAudit.controller.ts:1129,
 * workbench.controller.ts:478), which at least set an `X-Branding-Warnings`
 * response header. This component reads `json?.data?.warnings` (the shape
 * the sibling AI-scores endpoint already uses, see
 * GapAnalysisAiFailureBanner.jsx) and will start rendering the moment a
 * backend follow-up adds `warnings: stamped.warnings` to that response --
 * it is inert until then. Backend is a separate concurrent wave (Policy
 * Hub) and is intentionally not touched here.
 */
function DelegateSubmitWarningsBanner({ warnings, onDismiss }) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;
  return (
    <Alert
      type="warning"
      showIcon
      closable
      onClose={onDismiss}
      message="Your submission was recorded. Some items may need advisor review."
      description={
        <div>
          <p style={{ margin: "0 0 6px" }}>
            This is for your awareness only -- no action is required from you, and your
            advisor will review these items. You may re-submit later if you make changes.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((w, i) => (
              <li key={`submit-warn-${i}`} style={{ fontSize: 12 }}>
                {w}
              </li>
            ))}
          </ul>
        </div>
      }
      style={{ marginTop: 12 }}
    />
  );
}

export function DelegateDocxEditorPanel({
  projectId,
  templateRowId,
  brandedBlobUrl,
  fileLabel,
  draftHtmlFromWizard,
  submittedAt,
  filledBlobUrl,
  openBlobUrl,
  getApiToken,
  onRefresh,
  /**
   * Optional client-side logo overlay (Commandment VIII — backlog #39). Mammoth
   * (blob-docx-preview-html, called below) does not render DOCX headers/footers, so a
   * header-placed logo never reaches this preview HTML even though it renders correctly
   * in the real downloaded artefact. Callers that already have Company.branding in hand
   * (see persistableBlobUrl.js's resolveCompanyLogoUrl) may pass it here; existing callers
   * that omit it keep today's behavior unchanged.
   */
  logoUrl,
}) {
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState(null);
  const [initialHtml, setInitialHtml] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  /** stamped.warnings from the last successful submit response (Task #60) -- see
   * DelegateSubmitWarningsBanner for the shape + the backend-gap note. */
  const [submitWarnings, setSubmitWarnings] = useState([]);
  const readOnly = !!submittedAt;

  const loadHtml = useCallback(async () => {
    if (!brandedBlobUrl) return;
    setLoading(true);
    setFetchErr(null);
    try {
      if (draftHtmlFromWizard && String(draftHtmlFromWizard).trim()) {
        setInitialHtml(draftHtmlFromWizard);
        setLoading(false);
        return;
      }
      const token = await getApiToken();
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/blob-docx-preview-html?url=${encodeURIComponent(brandedBlobUrl)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const json = await res.json().catch(() => ({}));
      const html = json?.data?.html;
      if (!res.ok || !json.success || typeof html !== "string") {
        setFetchErr("Could not load document for editing.");
        setInitialHtml("");
        setLoading(false);
        return;
      }
      setInitialHtml(html);
    } catch (e) {
      setFetchErr(e?.message || "Load failed.");
      setInitialHtml("");
    } finally {
      setLoading(false);
    }
  }, [brandedBlobUrl, draftHtmlFromWizard, getApiToken]);

  useEffect(() => {
    void loadHtml();
  }, [loadHtml, templateRowId]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || loading) return;
    if (initialHtml != null) {
      el.innerHTML = initialHtml;
      setDirty(false);
    }
  }, [initialHtml, loading, templateRowId]);

  const handleSaveDraft = async () => {
    if (readOnly) return;
    const el = editorRef.current;
    if (!el) return;
    setSaving(true);
    try {
      const token = await getApiToken();
      const html = el.innerHTML;
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/organizational-setup/company-artefact-delegate-draft`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ templateRowId: String(templateRowId), html }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json?.error?.message || "Save failed.");
        return;
      }
      setDirty(false);
      message.success("Draft saved.");
      if (onRefresh) await onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (readOnly) return;
    const el = editorRef.current;
    if (!el) return;
    setSaving(true);
    try {
      const token = await getApiToken();
      const html = el.innerHTML;
      const draftRes = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/organizational-setup/company-artefact-delegate-draft`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ templateRowId: String(templateRowId), html }),
        },
      );
      const draftJson = await draftRes.json().catch(() => ({}));
      if (!draftRes.ok || !draftJson.success) {
        message.error(draftJson?.error?.message || "Could not save before submit.");
        return;
      }
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/organizational-setup/company-artefact-delegate-submit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ templateRowId: String(templateRowId) }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json?.error?.message || "Submit failed.");
        return;
      }
      message.success("Document submitted.");
      setDirty(false);
      // Task #60 -- non-blocking transparency: surface stamped.warnings if the
      // backend response carries any. See DelegateSubmitWarningsBanner for why
      // this is currently inert (backend does not yet return this field here).
      const warnings = Array.isArray(json?.data?.warnings) ? json.data.warnings : [];
      setSubmitWarnings(warnings);
      if (onRefresh) await onRefresh();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-sm font-medium text-[color:var(--color-text-muted)]">
        Loading document…
      </div>
    );
  }

  if (fetchErr) {
    return (
      <div className="rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
        {fetchErr}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={readOnly || saving}
          onClick={() => void handleSaveDraft()}
          className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50"
        >
          Save draft
        </button>
        <button
          type="button"
          disabled={readOnly || saving}
          onClick={() => void handleSubmit()}
          className="rounded-xl bg-[color:var(--color-accent-fill)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:opacity-50"
        >
          Submit
        </button>
        {readOnly && filledBlobUrl && openBlobUrl ? (
          <button
            type="button"
            onClick={() => {
              // openFilledArtefactUrl resolves false (never a raw/unsigned URL) when
              // re-signing fails or throws — surface that, don't fail silent.
              void openFilledArtefactUrl(filledBlobUrl, openBlobUrl).then((ok) => {
                if (ok === false) {
                  message.error('A secure link could not be generated. Please retry.');
                }
              });
            }}
            className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)]"
          >
            Download submitted
          </button>
        ) : null}
        {readOnly ? (
          <span className="text-xs font-semibold text-[color:var(--color-ok-fg)]">Submitted - editing locked.</span>
        ) : dirty ? (
          <span className="text-xs font-medium text-[color:var(--color-warn-fg)]">Unsaved edits</span>
        ) : null}
      </div>
      <DelegateSubmitWarningsBanner
        warnings={submitWarnings}
        onDismiss={() => setSubmitWarnings([])}
      />
      <div className="min-h-[360px] flex-1 overflow-y-auto rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-inner">
        <div className="relative">
          {logoUrl ? (
            // Commandment VIII — see logoUrl prop comment above. Sibling of (not inside)
            // the contentEditable node, so it is never captured by el.innerHTML and never
            // leaks into a saved draft or the submitted artefact.
            <img
              src={logoUrl}
              alt="Company Logo"
              className="pointer-events-none absolute left-6 top-4 h-10 w-auto max-w-[180px] object-contain"
            />
          ) : null}
          <div
            ref={editorRef}
            contentEditable={!readOnly}
            suppressContentEditableWarning
            className={`${DOCX_PREVIEW_BODY_CLASS} min-h-[360px] px-6 py-5 text-[color:var(--color-text-hero)] outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]`}
            onInput={() => setDirty(true)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Full-page delegate workspace: list assigned branded DOCX rows + in-panel editor (not a modal).
 */
export function DelegateCompanyArtefactWorkspace({ projectId, user, getApiToken, fetchProjectProfile, wiz, artefactIndexRows, brandedBlobUrlByRowId }) {
  const { openBlobUrl } = useApp();
  const released = isCompanyArtefactsReleasedToDelegates(wiz);
  const assignDelMap = useMemo(() => {
    const m = wiz.companyArtefactAssigneeDelegateMemberIdByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz.companyArtefactAssigneeDelegateMemberIdByRowId]);

  const draftMap = useMemo(() => {
    const m = wiz.companyArtefactDelegateDraftHtmlByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz.companyArtefactDelegateDraftHtmlByRowId]);

  const submittedMap = useMemo(() => {
    const m = wiz.companyArtefactDelegateSubmittedAtByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz.companyArtefactDelegateSubmittedAtByRowId]);

  const filledBlobUrlByRowId = useMemo(() => {
    const m = wiz.companyArtefactFilledBlobUrlByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz.companyArtefactFilledBlobUrlByRowId]);

  const [membersCache, setMembersCache] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!projectId || !getApiToken) return;
      try {
        const token = await getApiToken();
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${encodeURIComponent(projectId)}/members`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && json.success && Array.isArray(json.data?.members)) {
          setMembersCache(json.data.members);
        }
      } catch {
        if (!cancelled) setMembersCache([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, getApiToken]);

  const myId = useMemo(() => {
    const em = (user?.email || "").trim().toLowerCase();
    const m = membersCache.find((x) => (x.email || "").toLowerCase() === em);
    return m?.id || null;
  }, [membersCache, user?.email]);

  const myRows = useMemo(() => {
    if (!released || !myId) return [];
    const rows = Array.isArray(artefactIndexRows) ? artefactIndexRows : [];
    return rows.filter((row) => {
      const tid = String(row.id);
      if (!brandedBlobUrlByRowId[tid]) return false;
      const assignedUid = assignDelMap[tid];
      return assignedUid != null && String(assignedUid) === String(myId);
    });
  }, [released, myId, artefactIndexRows, brandedBlobUrlByRowId, assignDelMap]);

  const [selectedId, setSelectedId] = useState(null);
  const [bulkSubmitRunning, setBulkSubmitRunning] = useState(false);
  /** Which row the bulk job is currently POSTing (for list hint). */
  const [bulkSubmitCurrentRowId, setBulkSubmitCurrentRowId] = useState(null);
  /** Task #60 -- rows whose submit response carried stamped.warnings, collected
   * across a "Submit all" run. See DelegateSubmitWarningsBanner for the shape +
   * the note that this is currently inert until backend returns the field. */
  const [bulkWarningsSummary, setBulkWarningsSummary] = useState([]);

  useEffect(() => {
    if (myRows.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !myRows.some((r) => String(r.id) === String(selectedId))) {
      setSelectedId(String(myRows[0].id));
    }
  }, [myRows, selectedId]);

  const refresh = useCallback(async () => {
    if (fetchProjectProfile && projectId) await fetchProjectProfile(projectId);
  }, [fetchProjectProfile, projectId]);

  /** Unsubmitted .docx rows only (API does not support PDF submit). */
  const submitAllTargets = useMemo(() => {
    return myRows.filter((row) => {
      const id = String(row.id);
      if (submittedMap[id]) return false;
      const url = brandedBlobUrlByRowId[id];
      const label = artefactFileBasename(row);
      return isLikelyDocxFile(label, url);
    });
  }, [myRows, submittedMap, brandedBlobUrlByRowId]);

  const handleSubmitAll = useCallback(() => {
    if (bulkSubmitRunning) return;
    const targets = submitAllTargets;
    if (targets.length === 0) {
      message.info("No unsubmitted Word assignments to submit.");
      return;
    }
    Modal.confirm({
      title: "Submit all Word documents?",
      content:
        "This runs the same action as each document’s Submit button, in list order. Assignments without a saved draft submit the branded template. Unsaved edits in the editor are not included - use Save draft on the open document first if needed. You can leave this page-a progress message will show until finished.",
      okText: "Submit all",
      cancelText: "Cancel",
      onOk() {
        // Do not return a Promise: Ant Design would keep the dialog in a loading state for the whole batch
        // (if a request hangs, the modal looks frozen for minutes).
        void (async () => {
          setBulkSubmitRunning(true);
          setBulkWarningsSummary([]);
          message.open({
            type: "loading",
            content: `Submitting 0 / ${targets.length}…`,
            duration: 0,
            key: "delegate-bulk-submit",
          });
          try {
            const token = await getApiToken();
            let successCount = 0;
            for (let i = 0; i < targets.length; i++) {
              const row = targets[i];
              const id = String(row.id);
              message.open({
                type: "loading",
                content: `Submitting ${i + 1} / ${targets.length}…`,
                duration: 0,
                key: "delegate-bulk-submit",
              });
              setBulkSubmitCurrentRowId(id);
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), DELEGATE_SUBMIT_FETCH_TIMEOUT_MS);
              try {
                const { res, json } = await fetchPostSubmitDelegate(projectId, id, token, controller.signal);
                if (!res.ok || !json.success) {
                  message.error(json?.error?.message || `Submit failed for this assignment (${id}).`);
                  return;
                }
                successCount += 1;
                // Task #60 -- non-blocking transparency: collect stamped.warnings per
                // row for the post-run summary banner, without interrupting the batch.
                const rowWarnings = Array.isArray(json?.data?.warnings) ? json.data.warnings : [];
                if (rowWarnings.length > 0) {
                  setBulkWarningsSummary((prev) => [
                    ...prev,
                    { id, title: row.title || artefactFileNameUiDisplay(row) || id, warnings: rowWarnings },
                  ]);
                }
                await refresh();
              } catch (e) {
                const aborted =
                  e?.name === "AbortError" ||
                  (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "AbortError");
                if (aborted) {
                  const mins = Math.max(1, Math.round(DELEGATE_SUBMIT_FETCH_TIMEOUT_MS / 60000));
                  message.error(
                    `No response after about ${mins} min (${id}). The server may be busy-try submitting this document only, or try again later.`,
                  );
                } else {
                  message.error(e?.message || `Submit failed for (${id}).`);
                }
                return;
              } finally {
                clearTimeout(timeoutId);
              }
            }
            if (successCount > 0) {
              message.success(
                successCount === targets.length
                  ? `Submitted ${successCount} document(s).`
                  : `Submitted ${successCount} of ${targets.length} document(s).`,
              );
            }
          } finally {
            message.destroy("delegate-bulk-submit");
            setBulkSubmitCurrentRowId(null);
            setBulkSubmitRunning(false);
          }
        })();
      },
    });
  }, [bulkSubmitRunning, getApiToken, projectId, refresh, submitAllTargets]);

  const selectedRow = useMemo(
    () => myRows.find((r) => String(r.id) === String(selectedId)) || null,
    [myRows, selectedId],
  );

  if (!released) {
    return (
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-8 shadow-xl">
        <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Company artefacts</h3>
        <p className="mt-3 text-sm font-medium text-[color:var(--color-text-muted)]">
          Your advisor will release branded documents here after company approval. You will then fill and submit your
          assigned templates in this workspace.
        </p>
      </div>
    );
  }

  if (!myId) {
    return (
      <div className="rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] p-8 shadow-xl text-sm font-medium text-[color:var(--color-warn-fg)]">
        Could not resolve your account on this project. Open the project from the task list or contact your advisor.
      </div>
    );
  }

  if (myRows.length === 0) {
    return (
      <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
        <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Company artefacts</h3>
        <p className="mt-3 text-sm font-medium text-[color:var(--color-text-muted)]">
          No branded artefacts are assigned to you on this project. If you expect assignments, ask your advisor to
          assign your org-chart delegate row and release the package.
        </p>
      </div>
    );
  }

  const brandedUrl = selectedRow ? brandedBlobUrlByRowId[String(selectedRow.id)] : null;
  const labelForMime = selectedRow ? artefactFileBasename(selectedRow) : "";
  const labelUi = selectedRow ? artefactFileNameUiDisplay(selectedRow) : "";
  const isDocx = selectedRow && isLikelyDocxFile(labelForMime, brandedUrl);
  const isPdf = selectedRow && isLikelyPdfFile(labelForMime, brandedUrl);
  const tid = selectedRow ? String(selectedRow.id) : "";
  const submittedAt = tid ? submittedMap[tid] : null;
  const draftHtml = tid ? draftMap[tid] : null;
  const filledBlobUrl = tid ? filledBlobUrlByRowId[tid] : null;

  const rowStatus = (row) => {
    const id = String(row.id);
    if (bulkSubmitRunning && bulkSubmitCurrentRowId && id === String(bulkSubmitCurrentRowId)) return "Submitting…";
    if (submittedMap[id]) return "Submitted";
    if (draftMap[id]) return "In progress";
    return "Not started";
  };

  return (
    <>
      {bulkWarningsSummary.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setBulkWarningsSummary([])}
          message="Some submitted documents may need advisor review."
          description={
            <div>
              <p style={{ margin: "0 0 6px" }}>
                All documents in this run were recorded. This is for your awareness only --
                no action is required from you.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {bulkWarningsSummary.map((entry) => (
                  <li key={entry.id} style={{ fontSize: 12 }}>
                    <strong>{entry.title}:</strong> {entry.warnings.join("; ")}
                  </li>
                ))}
              </ul>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <div className="flex min-h-[560px] flex-col gap-6 lg:flex-row">
        <aside className="w-full shrink-0 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-4 shadow-sm lg:w-[300px]">
          <div className="flex flex-col gap-2">
            <p className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Your assignments</p>
            {submitAllTargets.length > 0 ? (
              <button
                type="button"
                disabled={bulkSubmitRunning}
                onClick={() => handleSubmitAll()}
                title="Submit every unsubmitted Word assignment in order (same as each row’s Submit)."
                className="rounded-lg border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] shadow-sm transition-colors hover:bg-[color:var(--color-ok-bg)] disabled:opacity-50"
              >
                {bulkSubmitRunning ? "Submitting…" : `Submit all (${submitAllTargets.length})`}
              </button>
            ) : null}
          </div>
          <ul className="mt-3 max-h-[480px] space-y-2 overflow-y-auto">
            {myRows.map((row) => {
              const id = String(row.id);
              const active = id === String(selectedId);
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(id)}
                    className={`w-full rounded-xl border px-3 py-3 text-left text-xs font-semibold transition-colors ${
                      active
                        ? "border-[color:var(--color-accent-fill)] bg-[color:var(--color-info-bg)] text-[color:var(--color-text-hero)]"
                        : "border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)] hover:bg-[color:var(--color-bg-alt)]"
                    }`}
                  >
                    <span className="line-clamp-2">{row.title || artefactFileNameUiDisplay(row) || id}</span>
                    <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                      {rowStatus(row)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
        <section className="flex min-h-[480px] min-w-0 flex-1 flex-col rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-xl">
          {selectedRow ? (
            <>
              <div className="border-b border-[color:var(--color-border)] pb-4">
                <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">
                  {selectedRow.title || labelUi || labelForMime || "Document"}
                </h3>
                <p className="mt-1 text-xs font-medium text-[color:var(--color-text-muted)]">{labelUi}</p>
              </div>
              <div className="mt-4 flex min-h-0 flex-1 flex-col">
                {isDocx ? (
                  <DelegateDocxEditorPanel
                    key={tid}
                    projectId={projectId}
                    templateRowId={tid}
                    brandedBlobUrl={brandedUrl}
                    fileLabel={labelForMime}
                    draftHtmlFromWizard={draftHtml}
                    submittedAt={submittedAt}
                    filledBlobUrl={filledBlobUrl}
                    openBlobUrl={openBlobUrl}
                    getApiToken={getApiToken}
                    onRefresh={refresh}
                  />
                ) : isPdf ? (
                  <div className="flex min-h-[400px] flex-1 flex-col gap-3">
                    <p className="text-sm font-medium text-[color:var(--color-text-muted)]">
                      In-app editing is available for Word (.docx) files. Preview this PDF below; ask your advisor if
                      a Word version is required.
                    </p>
                    <div className="min-h-[400px] flex-1 overflow-hidden rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)]">
                      <ArtefactTemplatePreview
                        blobUrl={brandedUrl}
                        fileLabel={labelForMime}
                        getApiToken={getApiToken}
                        forceLoad
                        fillParent
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex min-h-[400px] flex-1 flex-col gap-3">
                    <p className="text-sm font-medium text-[color:var(--color-text-muted)]">
                      In-app editing supports .docx. Preview only for this file type.
                    </p>
                    <div className="min-h-[400px] flex-1 overflow-hidden rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)]">
                      <ArtefactTemplatePreview
                        blobUrl={brandedUrl}
                        fileLabel={labelForMime}
                        getApiToken={getApiToken}
                        forceLoad
                        fillParent
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </section>
      </div>
    </>
  );
}
